import {Chip} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {useQuery} from "@tanstack/react-query";
import {createFileRoute, Link} from '@tanstack/react-router'
import React, {useState} from "react";
import {apiRetrieveImproved} from "../../../api";
import Header from "../../../Header.tsx";
import {deviceLabel} from "../../../utilities/deviceLabel.ts";

export const Route = createFileRoute('/_app/master/client-errors')({
    component: RouteComponent,
})

type ClientErrorRow = {
    id: number;
    type: 'js_error' | 'unhandled_rejection' | 'api_error' | 'rage_click';
    message: string;
    url?: string | null;
    stack?: string | null;
    meta?: Record<string, any> | null;
    user_agent?: string | null;
    created_at?: string;
    user?: {uid: string; name?: string; lastname?: string; email?: string} | null;
};

const TYPE_META: Record<string, {label: string; color: 'danger' | 'warning' | 'primary' | 'secondary'}> = {
    js_error:            {label: 'JS error', color: 'danger'},
    unhandled_rejection: {label: 'Rejection', color: 'warning'},
    api_error:           {label: 'API error', color: 'primary'},
    rage_click:          {label: 'Rage click', color: 'secondary'},
};

function StatCard({label, value, sub}: {label: string; value: React.ReactNode; sub?: string}) {
    return (
        <div className="rounded-xl border border-default-200 bg-content1 p-4">
            <div className="text-2xl font-bold">{value}</div>
            <div className="text-sm text-default-500">{label}</div>
            {sub && <div className="text-xs text-default-400">{sub}</div>}
        </div>
    );
}

function RouteComponent() {
    const [typeFilter, setTypeFilter] = useState<string>('');
    const [expanded, setExpanded] = useState<number | null>(null);

    const query = useQuery<{stats: {last_24h: number; last_7d: number; by_type_7d: Record<string, number>; affected_users_7d: number}; rows: ClientErrorRow[]}>({
        queryKey:        ['master-client-errors'],
        queryFn:         () => apiRetrieveImproved('master/client-errors'),
        refetchInterval: 60_000,
    });

    const stats = query.data?.stats;
    const rows = (query.data?.rows || []).filter((r) => !typeFilter || r.type === typeFilter);

    return (
        <>
            <Header />
            <div className="mx-auto w-11/12 max-w-6xl py-4">
                <h1 className="mb-1 text-2xl font-bold">Client Errors</h1>
                <p className="mb-4 text-sm text-default-500">
                    Friction reported from users' browsers — JS errors, failed API calls, and rage clicks
                    (repeated clicks that did nothing). Auto-refreshes every minute.
                </p>

                {query.isLoading ? (
                    <div className="flex justify-center py-12"><Spinner size="lg" /></div>
                ) : (
                    <>
                        {stats && (
                            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                                <StatCard label="Last 24 hours" value={stats.last_24h} />
                                <StatCard label="Last 7 days" value={stats.last_7d} />
                                <StatCard label="Users affected (7d)" value={stats.affected_users_7d} />
                                <StatCard label="Rage clicks (7d)" value={stats.by_type_7d?.rage_click || 0} />
                            </div>
                        )}

                        <div className="mb-3 flex flex-wrap gap-2">
                            <Chip variant={typeFilter === '' ? 'solid' : 'flat'} className="cursor-pointer" onClick={() => setTypeFilter('')}>All</Chip>
                            {Object.entries(TYPE_META).map(([key, m]) => (
                                <Chip key={key} color={m.color} variant={typeFilter === key ? 'solid' : 'flat'}
                                      className="cursor-pointer" onClick={() => setTypeFilter(typeFilter === key ? '' : key)}>
                                    {m.label}{stats?.by_type_7d?.[key] ? ` · ${stats.by_type_7d[key]}` : ''}
                                </Chip>
                            ))}
                        </div>

                        {rows.length === 0 ? (
                            <div className="py-12 text-center text-default-500">No client errors recorded. 🎉</div>
                        ) : (
                            <div className="overflow-x-auto rounded-xl border border-default-200">
                                <table className="w-full min-w-[900px] text-sm">
                                    <thead>
                                        <tr className="border-b border-default-200 bg-default-50 text-left text-xs uppercase tracking-wider text-default-500">
                                            <th className="px-3 py-2">When</th>
                                            <th className="px-3 py-2">Type</th>
                                            <th className="px-3 py-2">Message</th>
                                            <th className="px-3 py-2">Page</th>
                                            <th className="px-3 py-2">User</th>
                                            <th className="px-3 py-2">Device</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => {
                                            const tm = TYPE_META[r.type] || {label: r.type, color: 'default' as any};
                                            const page = r.url ? r.url.replace(/^https?:\/\/[^/]+/, '') : '—';
                                            return (
                                                <React.Fragment key={r.id}>
                                                    <tr className="cursor-pointer border-b border-default-100 last:border-0 hover:bg-default-50"
                                                        onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                                                        <td className="whitespace-nowrap px-3 py-2 text-default-500">
                                                            {r.created_at ? new Date(r.created_at).toLocaleString(undefined, {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'}) : '—'}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-2">
                                                            <Chip color={tm.color} size="sm" variant="flat">{tm.label}</Chip>
                                                        </td>
                                                        <td className="max-w-[340px] truncate px-3 py-2" title={r.message}>{r.message}</td>
                                                        <td className="max-w-[200px] truncate px-3 py-2 text-default-500" title={r.url || ''}>{page}</td>
                                                        <td className="whitespace-nowrap px-3 py-2">
                                                            {r.user
                                                                ? <Link to={`/master/players/${r.user.uid}` as any} className="text-primary" onClick={(e) => e.stopPropagation()}>
                                                                    {[r.user.name, r.user.lastname].filter(Boolean).join(' ') || r.user.email}
                                                                  </Link>
                                                                : <span className="text-default-300">guest</span>}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-default-500">{deviceLabel(r.user_agent)}</td>
                                                    </tr>
                                                    {expanded === r.id && (
                                                        <tr className="border-b border-default-100 bg-default-50/50">
                                                            <td colSpan={6} className="px-4 py-3">
                                                                <div className="space-y-2 text-xs">
                                                                    {/* The list cell cuts a long message off; here it is whole. */}
                                                                    <div className="break-words"><span className="font-semibold">Message:</span> {r.message}</div>
                                                                    {r.url && <div><span className="font-semibold">URL:</span> {r.url}</div>}
                                                                    {r.meta && Object.keys(r.meta).length > 0 && (
                                                                        <div><span className="font-semibold">Meta:</span> {JSON.stringify(r.meta)}</div>
                                                                    )}
                                                                    {r.user_agent && <div><span className="font-semibold">Agent:</span> {r.user_agent}</div>}
                                                                    {r.stack && (
                                                                        <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded bg-default-100 p-2 font-mono">{r.stack}</pre>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
}
